import localforage from 'localforage';
import { addSeconds, isBefore } from 'date-fns';
import { __awaiter } from 'tslib';
import * as signalR from '@microsoft/signalr';

/** Reads a cookie. */
function readCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ')
            c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0)
            return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
    return null;
}
/** Deletes all cookies which can be deleted. */
function deleteAllCookies() {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        deleteCookie(name);
    }
}
/** Deletes a cookie. */
function deleteCookie(name) {
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
}

function checkDateObject(value) {
    return Object.prototype.toString.call(value) === '[object Date]';
}
// NOTE alternatief voor toISOString() omdat hierbij telkens een convertie gedaan wordt naar UTC + 0.
/** Converts a given date as it to an ISO string without converting to UTC +0. */
function convertToISOString(value) {
    if (!value)
        return value;
    return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), value.getHours(), value.getMinutes(), value.getSeconds(), value.getMilliseconds())).toISOString();
}

/** Deletes all indexed db databases. */
function deleteAllIndexedDb() {
    return new Promise((resolve) => {
        window.indexedDB
            .databases()
            .then((r) => {
            for (let i = 0; i < r.length; i++)
                window.indexedDB.deleteDatabase(r[i].name);
        })
            .then(() => {
            resolve();
        });
    });
}
/** Deletes an entire indexed db. */
function deleteIndexedDb(name) {
    window.indexedDB.deleteDatabase(name);
}
/** Gets the version of an indexed db. */
function getIndexedDbVersion(name) {
    return new Promise((resolve) => {
        const request = window.indexedDB.open(name);
        request.onsuccess = (event) => {
            const db = event.target.result;
            // close db because we need to do an upgrade
            db.close();
            resolve(event.target.result.version);
        };
    });
}
/*** Gets information about a given indexed db. */
function getIndexedDbInfo(name) {
    return new Promise((resolve) => {
        const request = window.indexedDB.open(name);
        request.onsuccess = (event) => {
            const db = event.target.result;
            // close db because we need to do an upgrade
            db.close();
            resolve({
                version: event.target.result.version,
                objectStoreNames: [...db.objectStoreNames]
            });
        };
    });
}
/** Internal method to delete all object stores in a given indexed db with the actual version. */
function _deleteIndexedDbObjectStores(name, version, storeNames) {
    return new Promise((resolve) => {
        const request = indexedDB.open(name, version);
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            storeNames.forEach((storeName) => {
                db.deleteObjectStore(storeName);
            });
            db.close();
            resolve();
        };
    });
}

// http://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
/**
 * Generate universally unique identifier.
 */
function generateUuid() {
    let d = Date.now(); // support from IE9 onwards
    if (window.performance && typeof window.performance.now === 'function') {
        d += performance.now(); //use high-precision timer if available
    }
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
    return uuid;
}
/**
 * Will get the value or execute the function to get the value.
 * @param value
 */
function resolveValue(value) {
    if (typeof value === 'function') {
        return value();
    }
    else {
        return value;
    }
}
/**
 * Ensures the creation of a global variable with nesting and gives the reference to the last property.
 * @param nestedObject
 */
function ensureGlobal(nestedObject) {
    let result = null;
    if (nestedObject != null && nestedObject.length > 0) {
        let global = window;
        const pieces = nestedObject.split('.');
        pieces.forEach((piece) => {
            if (global[piece] == null) {
                global[piece] = {};
            }
            global = global[piece];
        });
        result = global;
    }
    return result;
}
// https://www.geeksforgeeks.org/how-to-get-the-javascript-function-parameter-names-values-dynamically/
/** Gets the parameters of a function. */
function getParamNames(func) {
    // String representaation of the function code
    let str = func.toString();
    // Remove comments of the form /* ... */
    // Removing comments of the form //
    // Remove body of the function { ... }
    // removing '=>' if func is arrow function
    str = str
        .replace(/\/\*[\s\S]*?\*\//g, '')
        .replace(/\/\/(.)*/g, '')
        .replace(/{[\s\S]*}/, '')
        .replace(/=>/g, '')
        .trim();
    // Start parameter names after first '('
    const start = str.indexOf('(') + 1;
    // End parameter names is just before last ')'
    const end = str.length - 1;
    const result = str.substring(start, end).split(', ');
    const params = [];
    result.forEach((element) => {
        // Removing any default value
        element = element.replace(/=[\s\S]*/g, '').trim();
        if (element.length > 0)
            params.push(element);
    });
    return params;
}
function getStack() {
    try {
        throw new Error();
    }
    catch (e) {
        return e.stack.replace('Error\n', '').trim();
    }
}
/** Compares 2 versions end returns 0 if equal, 1 if version 1 is higher than 2 and -1 if version 1 is lower than 2. */
function versionCompare(v1, v2, options = undefined) {
    const lexicographical = options && options.lexicographical;
    const zeroExtend = options && options.zeroExtend;
    let v1parts = v1.split('.');
    let v2parts = v2.split('.');
    function isValidPart(x) {
        return (lexicographical ? /^\d+[A-Za-z]*$/ : /^\d+$/).test(x);
    }
    if (!v1parts.every(isValidPart) || !v2parts.every(isValidPart)) {
        return NaN;
    }
    if (zeroExtend) {
        while (v1parts.length < v2parts.length)
            v1parts.push('0');
        while (v2parts.length < v1parts.length)
            v2parts.push('0');
    }
    if (!lexicographical) {
        v1parts = v1parts.map(Number);
        v2parts = v2parts.map(Number);
    }
    for (let i = 0; i < v1parts.length; ++i) {
        if (v2parts.length == i) {
            return 1;
        }
        if (v1parts[i] == v2parts[i]) {
            continue;
        }
        else if (v1parts[i] > v2parts[i]) {
            return 1;
        }
        else {
            return -1;
        }
    }
    if (v1parts.length != v2parts.length) {
        return -1;
    }
    return 0;
}
// NOTE from BARVUY
function generateRandomNumber(min, max) {
    // find diff
    const difference = max - min;
    // generate random number
    let rand = Math.random();
    // multiply with difference
    rand = Math.floor(rand * difference);
    // add with min value
    return rand + min;
}

class BarStopwatch {
    constructor() {
        this.id = null;
        /** Duration of the watch */
        this.duration = null;
    }
    /** Starts the stopwatch */
    start() {
        this.reset();
        if (window.performance && window.performance.timing) {
            this.id = generateUuid();
            window.performance.mark(this.id + '_start');
        }
    }
    /** Ends the stopwatch and defines the duration */
    stop() {
        if (window.performance && window.performance.timing && this.id != null) {
            window.performance.mark(this.id + '_end');
            window.performance.measure(this.id, this.id + '_start', this.id + '_end');
            const entries = window.performance.getEntriesByName(this.id);
            window.performance.clearMarks(this.id + '_end');
            window.performance.clearMeasures(this.id);
            // cleanup
            this.reset();
            this.duration = entries[0].duration;
        }
    }
    /** Resets the stopwatch */
    reset() {
        if (this.id != null) {
            window.performance.clearMarks(this.id + '_start');
            this.id = null;
            this.duration = null;
        }
    }
}

// Based on https://stackoverflow.com/a/4460624/2174621 + added circular dependency safety check
/**
 * Deep clones an object.
 * @param item The item to clone.
 */
function deepClone(item) {
    // we need to keep track of what we cloned
    const traversed = [];
    return deepCloneInternal(item, traversed);
}
function deepCloneInternal(item, traversed) {
    if (!item) {
        return item;
    } // null, undefined values check
    let types = [Number, String, Boolean], result;
    // normalizing primitives if someone did new String('aaa'), or new Number('444');
    types.forEach(function (type) {
        if (item instanceof type) {
            result = type(item);
        }
    });
    if (typeof result == 'undefined') {
        if (Object.prototype.toString.call(item) === '[object Array]') {
            result = [];
            item.forEach(function (child, index, array) {
                result[index] = deepClone(child);
            });
        }
        else if (typeof item == 'object') {
            // testing that this is DOM
            if (item.nodeType && typeof item.cloneNode == 'function') {
                result = item.cloneNode(true);
            }
            else if (!item.prototype) {
                // check that this is a literal
                if (item instanceof Date) {
                    result = new Date(item);
                }
                else {
                    // check if we already handled this object
                    const traversedItem = traversed.find((t) => t.originalItem == item);
                    if (traversedItem != null) {
                        // return the already cloned item
                        result = traversedItem.clonedItem;
                    }
                    else {
                        // it is an object literal
                        result = {};
                        // keep track of the clone
                        traversed.push({
                            originalItem: item,
                            clonedItem: result
                        });
                        for (const i in item) {
                            result[i] = deepCloneInternal(item[i], traversed);
                        }
                    }
                }
            }
            else {
                // depending what you would like here,
                // just keep the reference, or create new object
                if (item.constructor) {
                    // would not advice to do that, reason? Read below
                    result = new item.constructor();
                }
                else {
                    result = item;
                }
            }
        }
        else {
            result = item;
        }
    }
    return result;
}

/**
 * Gets an object containing the parts of a given URL.
 */
function extractUrlParts(url) {
    const a = document.createElement('a');
    a.href = url;
    return {
        url: a.href,
        host: a.host,
        hostname: a.hostname,
        port: a.port,
        pathname: a.pathname,
        protocol: a.protocol,
        hash: a.hash,
        search: a.search,
        // NOTE in IE the anchor gives undefined on origin, so we use a custom method
        origin: extractUrlOrigin(url)
    };
}
// https://stackoverflow.com/a/1420902/2174621
/**
 * Extracts the origin from a given URL.
 * @param url
 */
function extractUrlOrigin(url) {
    if (url == null || url.length == 0)
        return null;
    const pathArray = url.split('/');
    const protocol = pathArray[0];
    const host = pathArray[2];
    return protocol + '//' + host;
}
/**
 * Checks whether a given URL is absolute.
 * @param url
 */
// https://stackoverflow.com/questions/10687099/how-to-test-if-a-url-string-is-absolute-or-relative
function checkUrlAbsolute(url) {
    return new RegExp('^(?:[a-z]+:)?//', 'i').test(url);
}
// https://stackoverflow.com/a/901144/2174621
/**
 * Gets a query string value.
 * @param name Query string key name.
 * @param url Optional URL.
 */
function parseQueryStringValue(name, url = null) {
    if (url == null)
        url = window.location.href;
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'), results = regex.exec(url);
    if (!results)
        return null;
    if (!results[2])
        return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}
/**
 * Resolves an url from the origin (domain) of the application.
 * @param url
 */
function resolveUrlFromOrigin(url) {
    return checkUrlAbsolute(url) ? url : document.location.origin + url;
}
/**
 * Opens an URI.
 * @param uri
 */
function openUrl(url, target = null) {
    const a = document.createElement('a');
    a.href = url;
    if (target != null)
        a.target = target;
    document.body.appendChild(a);
    a.click();
    a.parentNode.removeChild(a);
}
/** Checks whether we are in localhost modus. */
function checkLocalhost() {
    return window.location.hostname === 'localhost';
}
/** Concats different pieces of an url with slashes. */
function concatUrl(...urlSegments) {
    let result = null;
    if (urlSegments && urlSegments.length) {
        result = '';
        urlSegments.forEach((urlSegment) => {
            if (urlSegment && urlSegment.length) {
                if (result.length && !result.endsWith('/') && !urlSegment.startsWith('/'))
                    result += '/';
                else if (result.endsWith('/') && urlSegment.startsWith('/'))
                    result = result.substring(0, result.length - 1);
                result += urlSegment;
            }
        });
    }
    return result;
}

const STORAGE_KEY_PREFIX = 'lvflanders.bar.shared.';
const _BAAS_SHARED_CONSTANTS = {
    STORAGE_KEY: {
        BAAS_STATE_CONTEXT: STORAGE_KEY_PREFIX + 'baas-state-context',
        BAR_APPINFO_CONTEXT: STORAGE_KEY_PREFIX + 'bar-appinfo-context'
    }
};

const APP_INFO_COOKIE_NAME = 'domain.basisarchitectuur.appinfo';
/** Wrapper around the appinfo cookies which is not exposed. */
class BarAppInfoManager {
    /** Gets the app info. */
    getAppInfo() {
        const cookie = readCookie(APP_INFO_COOKIE_NAME);
        const obj = cookie == null ? null : JSON.parse(cookie);
        return obj == null
            ? null
            : {
                userId: obj.ToepUser,
                securityTicketGuid: obj.SecurityTicketGuid
            };
    }
}

class BarIdpError extends Error {
    constructor(message) {
        super(message); // (1)
        this.name = this.constructor.name;
        if (arguments.length > 1) {
            this.status = arguments[1]; // we houden de httpStatus bij
        }
    }
}
class BarIdpServerError extends BarIdpError {
}
class BarIdpClientError extends BarIdpError {
}

/** Options for idp client. */
class BarIdpClientOptions {
}
class BarAuthValues {
}
class BarIdentity {
}
class BarKlant {
}
var BarIdentityType;
(function (BarIdentityType) {
    BarIdentityType["Anoniem"] = "ANONIEM";
    BarIdentityType["CID"] = "CID";
    BarIdentityType["Windows"] = "WINDOWS";
    BarIdentityType["Cryptokey"] = "CRYPTOKEY";
    BarIdentityType["Klantnummer"] = "ALVNUMMER";
    BarIdentityType["Testprofiel"] = "TESTPROFIEL";
    BarIdentityType["Fas"] = "FAS";
})(BarIdentityType || (BarIdentityType = {}));

/*
 * De WebWorker Orchestrator zorgt voor het opstarten van de webworker.
 * Deze webworker zal om de 10 minuten (default waarde) de Idp validatie functie oproepen
 * Deze kan een config object ontvangen met de baseUrl en initiele Timeout setting
 */
class BarIdpWebworkerOrchestrator {
    constructor(idpUrl, wwUrl) {
        this.idpUrl = idpUrl;
        this.wwUrl = wwUrl;
        /* afhandelen van binnenkomende webworker messages */
        this.processMessage = function (event) {
            if (event) {
                const parsedData = JSON.parse(event.data);
                switch (Object.keys(parsedData)[0]) {
                    case 'INFO':
                        if (parsedData.INFO) {
                            console.info(`webworker INFO message : ${parsedData.INFO.message}`);
                        }
                        break;
                    case 'WARNING':
                        console.warn(`webworker WARNING message: ${parsedData.WARNING.message}`);
                        break;
                    case 'ERROR':
                        console.error(parsedData.ERROR);
                        break;
                }
            }
        };
        /* afhandelen van binnenkomende webworker errors */
        this.processError = function (errorEvent) {
            console.error(errorEvent);
            // bij catastrophale fout gaan we webworker stoppen (de webworker kan dit ook zelf door self.close() aan te roepen, maar wij doen het in de orchestrator via terminate()
            if (window.Worker && typeof this.idpww != 'undefined' && typeof this.idpww.terminate == 'function')
                this.idpww.terminate();
        };
    }
    // deze functie zorgt voor aanmaak van een idp webworker indien deze nog niet bestaat en initialiseert deze met ticketId, baseUrl, idpUrl, timeout
    init(ticketId, timeout) {
        try {
            if (window.Worker) {
                if (typeof this.idpww == 'undefined') {
                    // in localhost we just point to the same origin because we cannot add workers from other domains (we can use a proxy)
                    let workerUrl = checkLocalhost() ? window.location.origin : this.wwUrl;
                    if (!workerUrl.endsWith('/'))
                        workerUrl += '/';
                    workerUrl += 'scripts/idp-validation-worker/main.js';
                    this.idpww = new Worker(workerUrl);
                    if (typeof this.idpww.postMessage == 'function') {
                        if (timeout != null) {
                            this.idpww.postMessage(JSON.stringify({ SETTIMEOUT: timeout }));
                        }
                        this.idpww.postMessage(JSON.stringify({
                            INIT: {
                                idpUrl: this.idpUrl,
                                timeout: timeout
                            }
                        }));
                    }
                    else {
                        console.warn('initWebworker : Er kon geen message verstuurd worden naar de webworker');
                    }
                    this.idpww.onmessage = (ev) => this.processMessage(ev);
                    this.idpww.onerror = (ev) => this.processError(ev);
                }
                else {
                    console.warn('initWebworker : initialisatie niet uitgevoerd, we hebben reeds een webworker');
                }
            }
            else {
                document.getElementById('result').innerHTML = 'Sorry, uw browser ondersteunt geen Web Workers...';
                console.error('Sorry, uw browser ondersteunt geen Web Workers...');
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    setTimeout(timeout) {
        try {
            if (window.Worker && typeof this.idpww != 'undefined' && typeof this.idpww.postMessage == 'function')
                this.idpww.postMessage(JSON.stringify({ SETTIMEOUT: timeout }));
            else
                console.warn('setTimeout : Er kon geen message verstuurd worden naar de webworker');
        }
        catch (Error) {
            console.error('er ging iets mis voor setTimeout ' + timeout);
        }
    }
    getTimeout() {
        try {
            if (window.Worker && typeof this.idpww != 'undefined' && typeof this.idpww.postMessage == 'function')
                this.idpww.postMessage(JSON.stringify({ GETTIMEOUT: true }));
            else
                console.warn('getTimeout : Er kon geen message verstuurd worden naar de webworker');
        }
        catch (error) {
            console.error(error);
        }
    }
}

// https://stackoverflow.com/questions/15035473/destroying-webworkers
/* De authProviderclient is een security browser Relying party met volgende functies :
-  Authenticate(authValues);
    - redirect naar IdP (de redirect terug vanuit de IdP moet je niet opvangen, dit wordt afgehandeld door @BAR (ander backlog item))
    - het authValues object bevat alles values om de IdP aan te roepen  (Authenticatie via url met querystring parameters)
- Validate(guid);
    - deze zal web worker starten die om de 10 minuten de IdP validatie functie oproept (web api oproep)
    - een andere timeout kan meegegeven worden (in minuten)

*/
class BarIdpClient {
    constructor(options = new BarIdpClientOptions()) {
        this.options = options;
        this.appInfoMgr = new BarAppInfoManager();
    }
    get authenticated() {
        return this.appInfoCookie != null;
    }
    get authUrl() {
        if (this.options.idpUrl.endsWith('/'))
            return this.options.idpUrl + 'auth/token';
        else
            return this.options.idpUrl + '/auth/token';
    }
    get logoutUrl() {
        if (this.options.idpUrl.endsWith('/'))
            return this.options.idpUrl + 'auth/logout';
        else
            return this.options.idpUrl + '/auth/logout';
    }
    get appInfoCookie() {
        return this.appInfoMgr.getAppInfo();
    }
    /* Authenticatie voert een redirect uit naar de IdP en geeft de via config doorgegeven authenticatie waardes op
    (zoals identitytype, hoedanigheid, voorwie)
    Technische 'demonstratie' pagina : https://alv-dotnet4-d/a/idp/wui/auth/token?target=https://alv-dotnet4-d/a/idp/wui/tech
    Voorbeeld url 1 : https://alv-dotnet4-d/a/idp/wui/auth/token?target=https://alv-dotnet4-d/a/idp/wui/tech&identityType=ALVNUMMER&identityValue=00010001407&vw=00015782102&vwrc=LB
    Voorbeeld url 2 : https://alv-dotnet4-d/a/idp/wui/auth/token?target=https://alv-dotnet4-d/a/idp/wui/tech&identityType=WINDOWS&identityValue=GUNDAM
    */
    authenticate(config = {
        identity: undefined,
        target: this.options.targetUrl
    }) {
        var _a, _b;
        // Als er geen identity
        if (!config.identity) {
            if (this.options.zone === 'E') {
                config.identity = { type: BarIdentityType.Fas };
            }
            else {
                config.identity = { type: BarIdentityType.Windows };
            }
        }
        if (!this.options.idpUrl)
            throw new BarIdpClientError('De idpUrl is niet gedefinieerd.');
        let authUrl = this.authUrl;
        /* target of default */
        authUrl = this.appendQuerystring(authUrl, 'target', config.target || window.location.href);
        /* Identity is required */
        if (!config || !config.identity || !config.identity.type)
            throw Error('Fatale fout : er werd geen identiteitstype meegegeven');
        else
            authUrl = this.appendQuerystring(authUrl, 'identityType', config.identity.type);
        /* identityvalue */
        if (config && config.identity && config.identity.value)
            authUrl = this.appendQuerystring(authUrl, 'identityValue', config.identity.value);
        /* voorwie values */
        if (config && config.voorwie && config.voorwie.rolcode)
            authUrl = this.appendQuerystring(authUrl, 'vw', config.voorwie.klantnummer);
        if (config && config.voorwie && config.voorwie.rolcode)
            authUrl = this.appendQuerystring(authUrl, 'vwrc', config.voorwie.rolcode);
        /* hoedanigheid values */
        if (config && config.voorwie && config.hoedanigheid.klantnummer)
            authUrl = this.appendQuerystring(authUrl, 'vw', config.hoedanigheid.klantnummer);
        if (config && config.voorwie && config.hoedanigheid.rolcode)
            authUrl = this.appendQuerystring(authUrl, 'vwrc', config.hoedanigheid.rolcode);
        if (this.options.formatToken)
            authUrl = this.appendQuerystring(authUrl, 'formattoken', this.options.formatToken);
        (_a = window.sessionStorage) === null || _a === void 0 ? void 0 : _a.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT); //idp service is cookie-first, so state in session storage must be cleared
        (_b = window.localStorage) === null || _b === void 0 ? void 0 : _b.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT); //idp service is cookie-first, so state in session storage must be cleared
        window.location.replace(authUrl);
        return Promise.resolve();
    }
    authenticateWithAlvnr(identityValue) {
        return this.authenticate({
            identity: {
                type: BarIdentityType.Klantnummer,
                value: identityValue
            },
            target: this.options.targetUrl
        });
    }
    authenticateWithAnoniem() {
        return this.authenticate({
            identity: { type: BarIdentityType.Anoniem },
            target: this.options.targetUrl
        });
    }
    authenticateWithCBIuserId(identityValue) {
        return this.authenticate({
            identity: { type: BarIdentityType.CID, value: identityValue },
            target: this.options.targetUrl
        });
    }
    authenticateWithCryptoKey(identityValue) {
        return this.authenticate({
            identity: { type: BarIdentityType.Cryptokey, value: identityValue },
            target: this.options.targetUrl
        });
    }
    authenticateWithWindows(identityValue) {
        return this.authenticate({
            identity: { type: BarIdentityType.Windows, value: identityValue },
            target: this.options.targetUrl
        });
    }
    authenticateWithFas() {
        return this.authenticate({
            identity: { type: BarIdentityType.Fas },
            target: this.options.targetUrl
        });
    }
    authenticateWithTestProfiel(identityValue) {
        return this.authenticate({
            identity: {
                type: BarIdentityType.Testprofiel,
                value: identityValue
            },
            target: this.options.targetUrl
        });
    }
    // NOTE ticketid as an argument is obsolete and fixed with ng 18
    /* Deze functie start een webworker op die om de x minuten een validate functie zal aanroepen */
    validate(timeout, ticketId) {
        if (!this.options.wwUrl)
            throw new BarIdpClientError('De web worker URL is niet gedefinieerd.');
        this.wwMgr = new BarIdpWebworkerOrchestrator(this.options.idpUrl, this.options.wwUrl);
        if (timeout != null) {
            this.wwMgr.init(null, timeout);
        }
        else {
            this.wwMgr.init(null);
        }
    }
    logout(target) {
        let logoutUrl = this.logoutUrl;
        if (target) {
            if (!checkUrlAbsolute(target))
                target = concatUrl(window.location.origin, target);
            logoutUrl += `?target=${target}`;
        }
        // just perform redirect
        window.location.replace(logoutUrl);
        return Promise.resolve();
    }
    /*
    Voegt een querystring toe met de juiste separator
    */
    appendQuerystring(url, qsPrm, sqValue) {
        const separator = url.indexOf('?') > 0 ? '&' : '?';
        return url + separator + qsPrm + '=' + sqValue;
    }
}

class BarSharedBootstrapper {
    /** Launches the shared boostrapping. */
    launch() {
        // define global logging variables
        this.defineLogging();
    }
    defineLogging() {
        const log = ensureGlobal('lvflanders.bar.shared.log');
        log.enabled = {};
        Object.defineProperty(log.enabled, 'cacheStorage', {
            get() {
                const enabledStr = window.localStorage.getItem('lvflanders.bar.shared.log.enabled.cacheStorage');
                return enabledStr != null ? JSON.parse(enabledStr) : false;
            },
            set(newValue) {
                window.localStorage.setItem('lvflanders.bar.shared.log.enabled.cacheStorage', JSON.stringify(newValue));
            }
        });
        Object.defineProperty(log, 'format', {
            get() {
                const enabledStr = window.localStorage.getItem('lvflanders.bar.shared.log.format');
                return enabledStr || 'object';
            },
            set(newValue) {
                if (newValue)
                    newValue = newValue.toLowerCase();
                if (!newValue || newValue === 'json' || newValue === 'object')
                    window.localStorage.setItem('lvflanders.bar.shared.log.format', newValue);
                else
                    console.warn("can only set value to 'json', 'object' or nothing.");
            }
        });
    }
}

// @dynamic
class BarCacheEntryOptions {
    constructor() {
        /** Whether to store for the lifecycle of a session */
        this.session = true;
    }
    /** Gets the default cache entry options */
    static get default() {
        return { session: true };
    }
}

/** Generic internal logger to start log based on a flag in localstorage. */
class BarLogger {
    constructor(options) {
        this.options = options;
    }
    get enabled() {
        var _a;
        if (this._enabled == null) {
            if ((_a = this.options) === null || _a === void 0 ? void 0 : _a.category) {
                // if we have a flag to check by default it is not enabled.
                this._enabled = false;
                const item = window.localStorage.getItem(`lvflanders.bar.shared.log.enabled.${this.options.category}`);
                if (item != null && item.length > 0) {
                    // parse the string value
                    this._enabled = JSON.parse(item);
                }
                // if we don't pass on a flag to check in storage we just assume it is enabled
            }
            else
                this._enabled = true;
        }
        return this._enabled;
    }
    get format() {
        if (this._format == null) {
            const item = window.localStorage.getItem('lvflanders.bar.shared.log.format');
            if (item != null && item.length > 0) {
                switch (item.toLowerCase()) {
                    case 'json':
                        this._format = 'json';
                        break;
                    default:
                        this._format = 'object';
                        break;
                }
            }
        }
        return this._format;
    }
    /** Logs a message. */
    log(message) {
        var _a, _b, _c;
        if (this.enabled) {
            if (!message.category)
                message.category = (_a = this.options) === null || _a === void 0 ? void 0 : _a.category;
            if (!message.context)
                message.context = (_b = this.options) === null || _b === void 0 ? void 0 : _b.context;
            if (!message.stack)
                message.stack = getStack();
            if ((_c = this.options) === null || _c === void 0 ? void 0 : _c.correlation) {
                // if we gave a correlation as global option we mark it as being the parent
                if (message.correlation)
                    message.correlation.parent = this.options.correlation;
                // otherwise we just use it as the leaf correlation for the message
                else
                    message.correlation = this.options.correlation;
            }
            switch (this.format) {
                case 'json':
                    console.log(JSON.stringify(message));
                    break;
                default:
                    console.log(message);
                    break;
            }
        }
    }
}

class BarDefaultAppState {
}

// NOTE wrapper around LocalForage to be able to swap underlying mechanism in an easy fashion
/** Used to store data in an Indexed DB object store. */
class BarStore {
    constructor(name, storeName) {
        this._localforage = localforage.createInstance({
            name: name,
            storeName: storeName,
            // force indexed db, we are not interested in other drivers
            driver: localforage.INDEXEDDB
        });
        this.logger = new BarLogger({
            category: 'cacheStorage',
            context: {
                name: name,
                storeName: storeName,
                type: 'BarStore (LocalForage)'
            }
        });
    }
    /** Internally sets a correlation used for logging purposes. Not to be used outside this library. */
    set _correlation(value) {
        // define parent correlation (optionally)
        this.logger.options.correlation = value;
    }
    /**
     * Gets an item from the object store.
     * @param key Key.
     */
    getItem(key) {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: `Begin getItem with key '${key}'`, correlation: correlation });
        return this._localforage.getItem(key).finally(() => {
            this.logger.log({ message: `End getItem with key '${key}'`, correlation: correlation });
        });
    }
    /**
     * Sets an item in the object store.
     * @param key Key.
     * @param value Object value.
     */
    setItem(key, value) {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: `Begin setItem with key '${key}'`, correlation: correlation });
        return this._localforage.setItem(key, value).finally(() => {
            this.logger.log({ message: `End setItem with key '${key}'`, correlation: correlation });
        });
    }
    /**
     * Removes an item from the store.
     * @param key Key of item.
     */
    removeItem(key) {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: `Begin removeItem with key '${key}'`, correlation: correlation });
        return this._localforage.removeItem(key).finally(() => {
            this.logger.log({ message: `End removeItem with key '${key}'`, correlation: correlation });
        });
    }
    /** Drops the entire store. */
    drop() {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: 'Begin drop', correlation: correlation });
        return this._localforage.dropInstance().finally(() => {
            this.logger.log({ message: 'End drop', correlation: correlation });
        });
    }
    /** Clears the store but doesn't drop it (like it's hot). */
    clear() {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: 'Begin clear', correlation: correlation });
        return this._localforage.clear().finally(() => {
            this.logger.log({ message: 'End clear', correlation: correlation });
        });
    }
    close() {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: 'Begin close', correlation: correlation });
        const dbInfo = this._localforage._dbInfo;
        if (dbInfo) {
            const db = dbInfo.db;
            if (db)
                db.close();
        }
        this.logger.log({ message: 'End close', correlation: correlation });
    }
    /**
     * Iterates over all records in the store.
     * @param iteratee Iterator callback function.
     */
    iterate(iteratee) {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: 'Begin iterate', correlation: correlation });
        return this._localforage.iterate(iteratee).finally(() => {
            this.logger.log({ message: 'End iterate', correlation: correlation });
        });
    }
    /** Gets all keys in the store. */
    keys() {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: 'Begin get keys', correlation: correlation });
        return this._localforage.keys().finally(() => {
            this.logger.log({ message: 'End get keys', correlation: correlation });
        });
    }
}

/** Represents a cache entry stored into the cache which contains the data and the metadata. */
class BarCacheEntry {
}

/** Key-value wrapper around object store with caching. */
class BarCacheStore {
    constructor(name, storeName) {
        this.name = name;
        this.storeName = storeName;
        // NOTE we use double underscore because the internal property already has an underscore.
        this.__appState = new BarDefaultAppState();
        this.logger = new BarLogger({
            category: 'cacheStorage',
            context: {
                type: 'BarCacheStore',
                name: this.name,
                storeName: this.storeName
            }
        });
    }
    // NOTE we use underscore because internal
    /** Sets the app state. */
    set _appState(value) {
        this.__appState = value;
    }
    /** Gets the app state. */
    get _appState() {
        return this.__appState;
    }
    /** Internally sets a correlation used for logging purposes. Not to be used outside this library. */
    set _correlation(value) {
        // define parent correlation (optionally)
        this.logger.options.correlation = value;
    }
    /**
     * Gets an item from the cache or executes a callback to set it if not in the cache.
     * @param key Key.
     * @param getCallback Callback to get the value in case the value is not in the cache.
     */
    getOrSetItem(key, getCallback, options = BarCacheEntryOptions.default) {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: `Begin getOrSetItem with key '${key}'`, correlation: correlation });
        const instance = this.getStoreInstance(correlation);
        return new Promise((resolve) => {
            this.getItemInternal(instance, key).then((value) => {
                if (value != null) {
                    resolve(value);
                }
                else {
                    const handleResult = (data) => {
                        // put it in the cache
                        this.setItemInternal(instance, key, data, options).then(() => {
                            resolve(data);
                        });
                    };
                    // execute the callback
                    const result = getCallback();
                    if (result instanceof Promise) {
                        // if it's a promise we execute the promise
                        result.then((resultValue) => {
                            handleResult(resultValue);
                        });
                    }
                    else {
                        // not a promise or null just handle it
                        handleResult(result);
                    }
                }
            });
        }).finally(() => {
            instance.close();
            this.logger.log({ message: `End getOrSetItem with key '${key}'`, correlation: correlation });
        });
    }
    /** Gets an item from the cache. */
    getItem(key) {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: `Begin getItem with key '${key}'`, correlation: correlation });
        const instance = this.getStoreInstance(correlation);
        return this.getItemInternal(instance, key).finally(() => {
            instance.close();
            this.logger.log({ message: `End getItem with key '${key}'`, correlation: correlation });
        });
    }
    /** Sets an item in the cache with options. */
    setItem(key, value, options = BarCacheEntryOptions.default) {
        var _a;
        const correlation = { value: generateUuid() };
        this.logger.log({ message: `Begin setItem with key '${key}'`, correlation: correlation });
        const instance = this.getStoreInstance(correlation);
        const entry = new BarCacheEntry();
        entry.value = value;
        entry.timestamp = new Date();
        // compute the expiration based on ttl
        entry.expiration = options != null && options.ttl != null ? addSeconds(entry.timestamp, options.ttl) : null;
        entry.options = options;
        entry.sessionId = (_a = this._appState) === null || _a === void 0 ? void 0 : _a.sessionId;
        return new Promise((resolve) => {
            instance.setItem(key, entry).then(() => resolve());
        }).finally(() => {
            instance.close();
            this.logger.log({ message: `End setItem with key '${key}'`, correlation: correlation });
        });
    }
    /** Removes an item from the cache. */
    removeItem(key) {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: `Begin removeItem with key '${key}'`, correlation: correlation });
        const instance = this.getStoreInstance(correlation);
        return instance.removeItem(key).finally(() => {
            instance.close();
            this.logger.log({ message: `End removeItem with key '${key}'`, correlation: correlation });
        });
    }
    /** Clears all items from the cache. */
    clear() {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: 'Begin clear', correlation: correlation });
        const instance = this.getStoreInstance(correlation);
        return instance.clear().finally(() => {
            instance.close();
            this.logger.log({ message: 'End clear', correlation: correlation });
        });
    }
    /** Drops the cache. */
    drop() {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: 'Begin drop', correlation: correlation });
        const instance = this.getStoreInstance(correlation);
        return instance.drop().finally(() => {
            instance.close();
            this.logger.log({ message: 'End drop', correlation: correlation });
        });
    }
    /**
     * Invalidates the store by removing expired cache entries.
     * @param clearSession Whether to clear the session cache entries.
     */
    invalidate(clearSession = false) {
        const correlation = { value: generateUuid() };
        this.logger.log({ message: `Begin invalidate with clearSession = ${clearSession}`, correlation: correlation });
        const instance = this.getStoreInstance(correlation);
        return new Promise((resolve) => {
            // here we hold an array of all promises used to remove items from the cache
            const invalidationPromises = [];
            // we iterate through the entire store
            instance
                .iterate((entry, key) => {
                // now we check if the entry is expired or if the consumer wants to clear session entries
                if (this.checkExpired(entry) || (clearSession && entry.options != null && entry.options.session)) {
                    const invalidationPromise = this.removeItem(key);
                    invalidationPromises.push(invalidationPromise);
                }
            })
                .then(() => {
                Promise.all(invalidationPromises).then(() => {
                    instance.keys().then((keys) => {
                        // here we check if the store still has items
                        if (keys.length === 0) {
                            // no items anymore: drop the instance
                            instance.drop().then(() => {
                                // finalize process
                                resolve();
                            });
                        }
                        else {
                            // finalize process
                            resolve();
                        }
                    });
                });
            });
        }).finally(() => {
            instance.close();
            this.logger.log({ message: `End invalidate with clearSession = ${clearSession}`, correlation: correlation });
        });
    }
    checkExpired(entry) {
        var _a, _b;
        return (
        // expiration expired
        (entry.expiration != null && isBefore(entry.expiration, new Date())) ||
            // or session expired (entry from previous session) in case we can check this
            (((_a = entry.options) === null || _a === void 0 ? void 0 : _a.session) && ((_b = this._appState) === null || _b === void 0 ? void 0 : _b.sessionId) && entry.sessionId != this._appState.sessionId));
    }
    /*  NOTE store used interally (LocalForage)
        Create new instance of the store on every occasion to avoid problems with newer versions of the database.
        Object stores with caching can be created and removed dynamically. Keeping an instance open would cause issues.
    */
    getStoreInstance(correlation) {
        const store = new BarStore(this.name, this.storeName);
        store._correlation = correlation;
        return store;
    }
    getItemInternal(store, key) {
        return new Promise((resolve) => {
            store.getItem(key).then((entry) => {
                if (entry == null) {
                    resolve(null);
                }
                else {
                    // check if expired
                    if (this.checkExpired(entry)) {
                        // if expired remove it
                        store.removeItem(key).then(() => {
                            // and return nothing
                            resolve(null);
                        });
                    }
                    else {
                        // not expired value is valid
                        entry.lastAccess = new Date();
                        // update the entry again with last acess timestamp
                        store.setItem(key, entry).then(() => {
                            // finally return the value
                            resolve(entry.value);
                        });
                    }
                }
            });
        });
    }
    setItemInternal(store, key, value, options) {
        return new Promise((resolve) => {
            var _a;
            const entry = new BarCacheEntry();
            entry.value = value;
            entry.timestamp = new Date();
            // compute the expiration based on ttl
            entry.expiration = options != null && options.ttl != null ? addSeconds(entry.timestamp, options.ttl) : null;
            entry.sessionId = (_a = this._appState) === null || _a === void 0 ? void 0 : _a.sessionId;
            entry.options = options;
            store.setItem(key, entry).then(() => {
                resolve();
            });
        });
    }
}

/** Defines how to store a given item. */
var BarCacheStoreType;
(function (BarCacheStoreType) {
    /** Cache store on application level */
    BarCacheStoreType[BarCacheStoreType["Application"] = 0] = "Application";
    /** Cache store on user level. */
    BarCacheStoreType[BarCacheStoreType["User"] = 1] = "User";
    /** Cache store on global level. */
    BarCacheStoreType[BarCacheStoreType["Global"] = 2] = "Global";
    /** Cache store on application and user level. */
    BarCacheStoreType[BarCacheStoreType["ApplicationUser"] = 3] = "ApplicationUser";
})(BarCacheStoreType || (BarCacheStoreType = {}));

const DB_NAME_PREFIX = 'domain.lv.cache.';
const DB_NAME_APP = `${DB_NAME_PREFIX}app`;
const DB_NAME_USER = `${DB_NAME_PREFIX}user`;
const DB_NAME_APP_USER = `${DB_NAME_PREFIX}appUser`;
const DB_NAME_GLOBAL = `${DB_NAME_PREFIX}global`;
// GVK gets its own database to avoid conflicts, but invalidation logic will be integrated
const DB_NAME_GVK = `${DB_NAME_PREFIX}gvk`;
class BarCacheManager {
    constructor(appState = new BarDefaultAppState()) {
        this.appState = appState;
        this.logger = new BarLogger({ category: 'cacheStorage', context: 'BarCacheManager' });
    }
    /**
     * Gets a cache store with given store options.
     * @param options
     */
    getStore(options) {
        let name = null;
        let storeName = null;
        switch (options.storeType) {
            case BarCacheStoreType.Application: {
                name = DB_NAME_APP;
                storeName = options.appCode;
                break;
            }
            case BarCacheStoreType.User: {
                name = DB_NAME_USER;
                storeName = options.userId;
                break;
            }
            case BarCacheStoreType.ApplicationUser: {
                name = DB_NAME_APP_USER;
                storeName = `${options.appCode}_${options.userId}`;
                break;
            }
            case BarCacheStoreType.Global: {
                name = DB_NAME_GLOBAL;
                storeName = 'main';
                break;
            }
        }
        if (options.store != null) {
            // check if consumer wants separate store in the database
            storeName += `_${options.store}`;
        }
        const result = new BarCacheStore(name, storeName);
        // define the app state context to use (which is the one from this class) by assigning internal property
        result._appState = this.appState;
        return result;
    }
    /**
     *  Invalidates all caches.
     * @param clearSession Whether to clear all session cache entries.
     */
    invalidate(clearSession = false) {
        // use a guid to correlate all actions together for logging purposes
        const correlation = { value: generateUuid() };
        this.logger.log({ message: `Starting cache invalidation with argument clearSession = ${clearSession}`, correlation: correlation });
        return new Promise((resolve) => {
            Promise.all([
                this.invalidateDatabase(DB_NAME_APP, clearSession, correlation),
                this.invalidateDatabase(DB_NAME_USER, clearSession, correlation),
                this.invalidateDatabase(DB_NAME_APP_USER, clearSession, correlation),
                this.invalidateDatabase(DB_NAME_GLOBAL, clearSession, correlation),
                this.invalidateDatabase(DB_NAME_GVK, clearSession, correlation)
            ]).then(() => {
                this.logger.log({ message: 'Ending cache invalidation', correlation: correlation });
                resolve();
            });
        });
    }
    invalidateDatabase(name, clearSession, correlation) {
        this.logger.log({ message: `Starting database invalidation with name '${name}'`, correlation: correlation });
        return new Promise((resolve) => {
            this.logger.log({ message: 'Getting objectStoreNames for invalidation purposes', correlation: correlation });
            this.getObjectStoreNames(name).then((objectStoreNames) => {
                this.logger.log({ message: 'Retrieved objectStoreNames for invalidation purposes', correlation: correlation });
                const promises = [];
                objectStoreNames.forEach((storeName) => {
                    const store = new BarCacheStore(name, storeName);
                    store._correlation = correlation;
                    const promise = store.invalidate(clearSession);
                    promises.push(promise);
                });
                Promise.all(promises).then(() => {
                    this.logger.log({ message: 'Getting objectStoreNames for dropping purposes', correlation: correlation });
                    this.getObjectStoreNames(name).then((objectStoreNames) => {
                        this.logger.log({ message: 'Retrieved objectStoreNames for dropping purposes', correlation: correlation });
                        if (objectStoreNames.length == 0) {
                            // drop database in case empty
                            localforage
                                .dropInstance({
                                name: name
                            })
                                .then(function () {
                                resolve();
                            });
                        }
                        else {
                            this.logger.log({ message: `Ending database invalidation with name '${name}'`, correlation: correlation });
                            resolve();
                        }
                    });
                });
            });
        });
    }
    getObjectStoreNames(name) {
        return new Promise((resolve) => {
            // open up a connection
            const openRequest = indexedDB.open(name);
            openRequest.onsuccess = () => {
                const db = openRequest.result;
                // to read all object store names
                const objectStoreNames = db.objectStoreNames;
                db.close();
                resolve(this.toArray(objectStoreNames));
            };
        });
    }
    // https://stackoverflow.com/a/2735133/2174621
    toArray(obj) {
        const array = [];
        // iterate backwards ensuring that length is an UInt32
        for (let i = obj.length >>> 0; i--;) {
            array[i] = obj[i];
        }
        return array;
    }
}

class BarCacheStoreOptions {
}

const LOCALE = {
    FLEMISH: 'nl-be',
    DUTCH: 'nl'
};

/** Allow to hook into the error handling.. */
class BarErrorHandling {
    constructor() {
        this._delegates = [];
    }
    /** Register an error handler in the pipeline */
    registerDelegate(handler) {
        this._delegates.push(handler);
    }
    /** Gets the registered handlers. */
    get delegates() {
        return this._delegates;
    }
}
BarErrorHandling.instance = new BarErrorHandling();

var BarGvkCompatibilityMode;
(function (BarGvkCompatibilityMode) {
    // in version 1 we use Application storage and embed the user id in the key.
    /** Compatible with version 1 of Gebruikersvoorkeuren. */
    BarGvkCompatibilityMode[BarGvkCompatibilityMode["Version_1"] = 0] = "Version_1";
    // in version 2 we use Application User storage where the key is kept proper (by default this is used).
    /** Compatible with version 2 of Gebruikersvoorkeuren. */
    BarGvkCompatibilityMode[BarGvkCompatibilityMode["Version_2"] = 1] = "Version_2";
})(BarGvkCompatibilityMode || (BarGvkCompatibilityMode = {}));

/** Default state for GVK */
class BarGvkDefaultState {
    constructor() {
        // this is the relative Root URL from the root which can be configured
        this.apiRootUrl = `${window.origin}/basisarchitectuur/domain.basisarchitectuur.web/`;
        // whether remote calls on localhost to the remote api are enabled
        this.remoteLocalhostApiEnabled = !checkLocalhost();
        this.appInfoMgr = new BarAppInfoManager();
    }
    // TODO @lvflanders/bar should be statefull
    get appCode() {
        let result;
        // Q&D try get toepassing code from Angular context
        try {
            result = window.lvflanders.bar.ng.core.state.toepassing.code;
        }
        catch (_a) {
            result = null;
        }
        if (!result) {
            // Q&D try get toepassing code from MVC context
            try {
                result = window.domain.WebCore.basisContext.appCode();
            }
            catch (_b) {
                result = null;
            }
        }
        return result;
    }
    get userId() {
        const appInfo = this.appInfoMgr.getAppInfo();
        return appInfo == null ? null : appInfo.userId;
    }
}

/** GVK storage modus defining where state is persisted. */
var BarGvkStorageMode;
(function (BarGvkStorageMode) {
    /** GVK will use browser storage and remote storage (if accessible) to persist the state. */
    BarGvkStorageMode[BarGvkStorageMode["Default"] = 0] = "Default";
    /** GVK will only use browser storage and no remote storage to persist the state. */
    BarGvkStorageMode[BarGvkStorageMode["BrowserOnly"] = 1] = "BrowserOnly";
    /** GVK will only use remote storage and no browser storage to persist the state. */
    BarGvkStorageMode[BarGvkStorageMode["RemoteOnly"] = 2] = "RemoteOnly";
})(BarGvkStorageMode || (BarGvkStorageMode = {}));

class BarGvkDefaultStore {
    constructor(appCode, userId) {
        this.store = new BarCacheStore(DB_NAME_GVK, `${appCode}_${userId}`);
    }
    getItem(key) {
        return this.store.getItem(key);
    }
    setItem(key, value) {
        // by default we set an item for indefinite time in the cache
        return this.store.setItem(key, value, { session: false });
    }
    removeItem(key) {
        return this.store.removeItem(key);
    }
}

const DB_NAME_GVK_LEGACY = 'domain.gebruikersvoorkeuren';
class BarGvkLegacyStore {
    constructor(appCode, userId) {
        this.appCode = appCode;
        this.userId = userId;
    }
    getItem(key) {
        const realKey = this.generateRealKey(key);
        const store = this.getStoreInstance();
        return store.getItem(realKey).finally(() => {
            // always close the store to avoid IndexedDB locks
            store.close();
        });
    }
    setItem(key, value) {
        const realKey = this.generateRealKey(key);
        const store = this.getStoreInstance();
        return store.setItem(realKey, value).finally(() => {
            // always close the store to avoid IndexedDB locks
            store.close();
        });
    }
    removeItem(key) {
        const realKey = this.generateRealKey(key);
        const store = this.getStoreInstance();
        return store.removeItem(realKey).finally(() => {
            // always close the store to avoid IndexedDB locks
            store.close();
        });
    }
    generateRealKey(key) {
        // in legacy mode we integrate the user id into the key
        return `_${this.userId}_${key}`;
    }
    getStoreInstance() {
        return new BarStore(DB_NAME_GVK_LEGACY, this.appCode);
    }
}

/** retrieves baaS context header to window.sessionStorage */
class BaasContextUtil {
    /** returns stored baas context object in stringified format (used for baas.context.backend header)*/
    getBaasContextAsHeaderString() {
        const data = this.getBaasContextString();
        if (data == null)
            return '';
        return data;
    }
    /**get raw string from session or local storage */
    getBaasContextString() {
        let data = window.sessionStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT);
        if (data === null) {
            data = window.localStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT); //get it from local storage backup (and copy in sessionstorga) instead!
            if (data !== null)
                window.sessionStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT, data);
        }
        return data;
    }
    get baasHeaderName() {
        return 'baas.context.backend';
    }
}

// NOTE originates from GVK
/** Vanilla HTTP utility class (ajax). */
class HttpUtility {
    constructor(baasContextUtil = new BaasContextUtil()) {
        this.baasContextUtil = baasContextUtil;
    }
    /**
     * Performs an ajax GET operation.
     * @param url Url to invoke.
     * @param callback Callback when finished.
     */
    get(url, callback) {
        const xmlhttp = new XMLHttpRequest();
        xmlhttp.withCredentials = true;
        xmlhttp.onreadystatechange = () => {
            this.handleXmlHttpRequestDone(xmlhttp, callback);
        };
        xmlhttp.open('GET', url, true);
        xmlhttp.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        this.addBaasHeaders(xmlhttp);
        xmlhttp.send();
    }
    /**
     * Performs an ajax POST operation.
     * @param url Url to invoke.
     * @param params Params.
     * @param callback Callback when finished.
     */
    post(url, params, callback) {
        const xmlhttp = new XMLHttpRequest();
        xmlhttp.withCredentials = true;
        xmlhttp.onreadystatechange = () => {
            this.handleXmlHttpRequestDone(xmlhttp, callback);
        };
        xmlhttp.open('POST', url, true);
        xmlhttp.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xmlhttp.setRequestHeader('Content-type', 'application/json');
        this.addBaasHeaders(xmlhttp);
        xmlhttp.send(params);
    }
    /**
     * Performs an ajax PUT operation.
     * @param url Url to invoke.
     * @param params Params.
     * @param callback Callback when finished.
     */
    put(url, params, callback) {
        const xmlhttp = new XMLHttpRequest();
        xmlhttp.withCredentials = true;
        xmlhttp.onreadystatechange = () => {
            this.handleXmlHttpRequestDone(xmlhttp, callback);
        };
        xmlhttp.open('PUT', url, true);
        xmlhttp.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xmlhttp.setRequestHeader('Content-type', 'application/json');
        this.addBaasHeaders(xmlhttp);
        xmlhttp.send(params);
    }
    /**
     * Performs an ajax DELETE operation.
     * @param url Url to invoke.
     * @param params Params.
     * @param callback Callback when finished.
     */
    delete(url, params, callback) {
        const xmlhttp = new XMLHttpRequest();
        xmlhttp.withCredentials = true;
        xmlhttp.onreadystatechange = () => {
            this.handleXmlHttpRequestDone(xmlhttp, callback);
        };
        xmlhttp.open('DELETE', url, true);
        xmlhttp.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        this.addBaasHeaders(xmlhttp);
        xmlhttp.send(params);
    }
    /**
     * Checks whether a given response is an error response.
     * @param response HTTP response.
     */
    isErrorResponse(response) {
        if (response.status == 'fail' || response.status == 'error') {
            return true;
        }
        else if (response.hasErrors) {
            return true;
        }
        else if (response.error) {
            return true;
        }
        else if (response.errors) {
            return true;
        }
        else {
            return false;
        }
    }
    /**
     * Gets the error message from the response.
     * @param response HTTP response.
     */
    getErrorResponseMessage(response) {
        if (response.status == 'fail') {
            return 'A validation failed';
        }
        else if (response.status == 'error') {
            return response.message;
        }
        else if (response.error) {
            return response.message;
        }
        else if (response.errors) {
            return 'Error';
        }
        else {
            return null;
        }
    }
    /**
     * Gets the data from the response.
     * @param response HTTP response.
     */
    getResponseData(response) {
        if (response.data) {
            return response.data;
        }
        else {
            return response;
        }
    }
    /**
     * adds the baaS headers from session storage to GVK calls
     */
    addBaasHeaders(xmlhttp) {
        const ctx = this.baasContextUtil.getBaasContextAsHeaderString();
        if (ctx !== '')
            xmlhttp.setRequestHeader(this.baasContextUtil.baasHeaderName, ctx);
    }
    handleXmlHttpRequestDone(xmlhttp, callback) {
        if (xmlhttp.readyState == 4) {
            let response;
            // Indien 'response' geen 'hasErrors' of 'errors' property heeft dan is deze malformed.
            // In principe moet de GVK web API steeds (minimaal) deze properties teruggeven.
            if (!xmlhttp.responseText) {
                if (xmlhttp.status == 200) {
                    response = { status: 'success' };
                }
                else if (xmlhttp.status >= 400 && xmlhttp.status < 500) {
                    response = {
                        status: 'fail',
                        data: {
                            message: 'Status code ' + xmlhttp.status + ' returned.'
                        }
                    };
                }
                else {
                    response = {
                        status: 'error',
                        message: 'Status code ' + xmlhttp.status + ' returned.'
                    };
                }
            }
            else {
                let responseText = xmlhttp.responseText;
                if (responseText.indexOf(HttpUtility.ANTI_XSSI_TOKEN) == 0) {
                    if (responseText.length > HttpUtility.ANTI_XSSI_TOKEN.length) {
                        responseText = responseText.substring(HttpUtility.ANTI_XSSI_TOKEN.length);
                    }
                    else {
                        responseText = '';
                    }
                }
                response = JSON.parse(responseText);
            }
            if (callback) {
                callback(response);
            }
        }
    }
}
HttpUtility.ANTI_XSSI_TOKEN = ")]}',";

class BarGvkRemoteStore {
    constructor(urlRoot, appCode) {
        this.urlRoot = urlRoot;
        this.appCode = appCode;
        this.http = new HttpUtility();
    }
    /** Checks whether the remote storage is disabled. */
    checkDisabled() {
        return new Promise((resolve) => {
            // Opgelet: deze promise zal altijd 'resolven' en nooit rejecten.
            // Bij fouten wordt het zeker voor het onzeker genomen: de voorkeuren zullen niet geladen worden.
            this.http.get(`${this.urlRoot}/${this.appCode}/settings`, (jsonResponse) => {
                try {
                    if (this.http.isErrorResponse(jsonResponse)) {
                        resolve(true);
                    }
                    else {
                        resolve(this.http.getResponseData(jsonResponse).disabled);
                    }
                }
                catch (ex) {
                    console.error(ex);
                    resolve(true);
                }
            });
        });
    }
    getItem(key) {
        return new Promise((resolve, reject) => {
            this.http.get(`${this.urlRoot}/${this.appCode}?key=${encodeURIComponent(key)}`, (jsonResponse) => {
                try {
                    if (this.http.isErrorResponse(jsonResponse)) {
                        reject('Server side fout bij ophalen voorkeuren via LoadVoorkeuren: ' + this.http.isErrorResponse(jsonResponse));
                    }
                    else if (this.http.getResponseData(jsonResponse).hasVoorkeuren) {
                        resolve(this.http.getResponseData(jsonResponse).voorkeuren);
                    }
                    else {
                        resolve(undefined);
                    }
                }
                catch (ex) {
                    reject('Fout bij uitlezen van ajax LoadVoorkeuren response: ' + ex);
                }
            });
        });
    }
    setItem(key, value) {
        return new Promise((resolve, reject) => {
            this.http.put(`${this.urlRoot}/${this.appCode}?key=${encodeURIComponent(key)}`, value, (jsonResponse) => {
                try {
                    if (this.http.isErrorResponse(jsonResponse)) {
                        // #310620 just log a warning, don't reject entire save
                        console.error('Server side fout bij wegschrijven voorkeuren: ' + this.http.getErrorResponseMessage(jsonResponse));
                    }
                    resolve();
                }
                catch (ex) {
                    reject('Fout bij uitlezen van voorkeuren ajax response: ' + ex);
                }
            });
        });
    }
    removeItem(key) {
        return new Promise((resolve, reject) => {
            // En dan nu de backend verwittigen
            // TODO: optimalisatie -> meerdere setNames in één keer doorgeven.
            let optionalSetName = '';
            // userId hoeft niet, want dat regelt BAR (sessie)
            if (key)
                optionalSetName = `?key=${encodeURIComponent(key)}`;
            this.http.delete(`${this.urlRoot}/${this.appCode}${optionalSetName}`, null, (jsonResponse) => {
                try {
                    if (this.http.isErrorResponse(jsonResponse)) {
                        reject('Server side fout bij verwijderen van voorkeuren via ClearVoorkeuren: ' + this.http.getErrorResponseMessage(jsonResponse));
                    }
                    else {
                        resolve();
                    }
                }
                catch (ex) {
                    reject('Fout bij uitlezen van ajax ClearVoorkeuren response: ' + ex);
                }
            });
        });
    }
}

// NOTE this is based on the original GVK and is not exposed to the outside world
// API voor de 'storage' layer.
// De storage layer maakt gebruikt van een lokale storage (BarStorage) en remote storage (ProfielBeheer met laagje er bovenop).
class BarGvkInternalManager {
    constructor(state, remoteStore) {
        this.state = state;
        this.remoteStore = remoteStore;
        this.warningBrowserOnlyStorageTriggered = false;
        this.disabled = false;
    }
    get urlRoot() {
        // we hebben de root URL nodig waarop we onze calls gaan doen (domain + segment)
        let result = this.state.apiRootUrl || '';
        if (!result.endsWith('/'))
            result += '/';
        // hier maken we een assumptie dat de web api er relatief als volgt eruit ziet
        result += 'api/gvk';
        return result;
    }
    // Zijn de gebruikersvoorkeuren uitgeschakeld (via een 'master' switch in profielbeheer)?
    // Dit is van belang om voorkeuren te kunnen uitschakelen 'op afstand' indien er issues zijn.
    checkDisabled(loadRemoteSettings, appCode, userId) {
        return new Promise((resolve) => {
            if (this.disabled) {
                // if globally disabled we just exit
                resolve(true);
                return;
            }
            const uniqueKey = this.getUniqueKeyForSessionStorage(appCode, userId, '_disabled'), localSessionValue = window.sessionStorage.getItem(uniqueKey);
            if (localSessionValue === undefined || localSessionValue === null) {
                if (loadRemoteSettings) {
                    this.getRemoteStore(appCode)
                        .checkDisabled()
                        .then((disabled) => {
                        window.sessionStorage.setItem(uniqueKey, disabled.toString());
                        resolve(disabled);
                    });
                }
                else {
                    sessionStorage.setItem(uniqueKey, 'false');
                    resolve(false);
                }
            }
            else
                resolve(localSessionValue === 'true');
        });
    }
    // Gets the gebruikersvoorkeuren.
    getItem(appCode, userId, setName, storageMode, noSettingsAction, sets, forceGetFromRemoteStorage, compatibilityMode) {
        // confirm storage mode based on state (eventually change it if incompatiblity)
        storageMode = this.confirmStorageMode(storageMode);
        let returnPromise = Promise.resolve();
        // Remote storage heeft voorrang op local storage.
        // Let wel: remote storage wordt maar één keer bevraagd (bij start van sessie) en wordt dan ook lokaal bewaard.
        // Bij volgende 'loads' zal de resolve dus steeds een 'undefined' geven, maar dat is geen probleem omdat de settings
        // lokaal werden bewaard.
        if (storageMode !== BarGvkStorageMode.BrowserOnly) {
            returnPromise = new Promise((resolve, reject) => {
                returnPromise
                    .then((value) => {
                    const uniqueKey = this.getUniqueKeyForSessionStorage(appCode, userId, setName), localSessionValue = sessionStorage.getItem(uniqueKey);
                    if (localSessionValue === undefined || localSessionValue === null || forceGetFromRemoteStorage) {
                        // Onthouden dat we het al opgehaald hebben in deze sessie (of althans een poging gedaan hebben).
                        // userId moet niet opgegeven want daar zorgt BAR voor (sessie).
                        sessionStorage.setItem(uniqueKey, new Date().getTime().toString());
                        this.getRemoteStore(appCode)
                            .getItem(setName)
                            .then((data) => {
                            if (data != null) {
                                // Als profielbeheer voorkeuren heeft: lokaal bewaren.
                                this.setItem(appCode, userId, setName, BarGvkStorageMode.BrowserOnly, data, compatibilityMode).then(() => {
                                    resolve(data);
                                });
                            }
                            else {
                                // Profielbeheer heeft geen voorkeuren (meer): lokaal leegmaken.
                                this.removeItem(appCode, userId, setName, BarGvkStorageMode.BrowserOnly, compatibilityMode).then(() => {
                                    resolve(undefined);
                                });
                            }
                        });
                    }
                    else
                        resolve(undefined);
                })
                    .catch((error) => {
                    reject('Fout bij ophalen localPromise van get: ' + error);
                });
            });
        }
        // Indien er remote geen settings zijn of werden opgehaald, dan wordt local storage gecontroleerd.
        if (storageMode !== BarGvkStorageMode.RemoteOnly) {
            returnPromise = new Promise((resolve, reject) => {
                returnPromise
                    .then((value) => {
                    if (!value) {
                        this.getBrowserStore(appCode, userId, compatibilityMode)
                            .getItem(setName)
                            .then((value) => {
                            resolve(value);
                        })
                            .catch((error) => {
                            reject(error);
                        });
                    }
                    else
                        resolve(value);
                })
                    .catch((error) => {
                    reject(error);
                });
            });
        }
        // En indien er remote en/of local niets gevonden werd: dan kunnen de voorkeuren eventueel 'berekend' worden door een callback.
        if (noSettingsAction.action !== 'none') {
            returnPromise = new Promise((resolve, reject) => {
                returnPromise
                    .then((value) => {
                    if (!value) {
                        if (noSettingsAction.action === 'callback') {
                            const ctx = {
                                appCode: appCode,
                                setName: setName,
                                // TODO check concept of sets
                                settings: sets != null && sets.get(setName) ? sets.get(setName).settings : this.createEmptySettings(setName)
                            };
                            noSettingsAction.callback
                                .apply(null, [ctx])
                                .then((voorkeuren) => {
                                if (!voorkeuren) {
                                    resolve(undefined);
                                }
                                // Om gebruikers van deze API enigszins 'flexibiliteit' aan te bieden kunnen zij kiezen om een JSON string terug te geven of
                                // een gewoon JavaScript object.
                                let voorkeurenObject = null, voorkeurenString = null;
                                if (typeof voorkeuren === 'string') {
                                    voorkeurenObject = JSON.parse(voorkeuren);
                                    voorkeurenString = voorkeuren;
                                }
                                else if (voorkeuren instanceof String) {
                                    voorkeurenObject = JSON.parse(voorkeuren.toString());
                                    voorkeurenString = voorkeuren;
                                }
                                else {
                                    voorkeurenObject = voorkeuren;
                                    voorkeurenString = JSON.stringify(voorkeuren);
                                }
                                // Controle op 'well-formedness' van het object.
                                // Deze check zou nog doorgedrevener kunnen, maar dit is al goed genoeg.
                                if (voorkeurenObject._gvk_setName && voorkeurenObject._gvk_timeStamp) {
                                    this.setItem(appCode, userId, setName, storageMode, voorkeurenString, compatibilityMode);
                                    resolve(voorkeurenString);
                                }
                                else
                                    reject('Aanroep van noSettingsAction.callback heeft een foutief gestructureerd JSON object teruggegeven.');
                            })
                                .catch((error) => {
                                reject('Aanroep van noSettingsAction.callback heeft een fout teruggegeven: ' + error);
                            });
                        }
                    }
                    else
                        resolve(value);
                })
                    .catch((error) => {
                    reject(error);
                });
            });
        }
        return returnPromise;
    }
    // Bewaren van voorkeuren
    setItem(appCode, userId, setName, storageMode, voorkeuren, compatibilityMode) {
        // confirm storage mode based on state (eventually change it if incompatiblity)
        storageMode = this.confirmStorageMode(storageMode);
        return new Promise((resolve, reject) => {
            if (storageMode == BarGvkStorageMode.BrowserOnly) {
                this.getBrowserStore(appCode, userId, compatibilityMode)
                    .setItem(setName, voorkeuren)
                    .then(() => {
                    resolve();
                });
            }
            else {
                // TODO take into account localStorageMode === 'off' ??
                this.getBrowserStore(appCode, userId, compatibilityMode)
                    .setItem(setName, voorkeuren)
                    .then(() => {
                    const uniqueKey = this.getUniqueKeyForSessionStorage(appCode, userId, setName);
                    // Onthouden dat we het opgestuurd hebben (of althans een poging gedaan hebben).
                    sessionStorage.setItem(uniqueKey, new Date().getTime().toString());
                    this.getRemoteStore(appCode)
                        .setItem(setName, voorkeuren)
                        .then(() => {
                        resolve();
                    })
                        .catch((error) => {
                        reject(error);
                    });
                });
            }
        });
    }
    // Opkuisen (clearen) van voorkeuren
    removeItem(appCode, userId, setName, storageMode, compatibilityMode) {
        // confirm storage mode based on state (eventually change it if incompatiblity)
        storageMode = this.confirmStorageMode(storageMode);
        // clearen gebeurt in de 2 storage systemen. Eventueel zou localStorageMode kunnen uitgelezen worden.
        const localPromise = this.getBrowserStore(appCode, userId, compatibilityMode).removeItem(setName);
        if (storageMode == BarGvkStorageMode.BrowserOnly) {
            return localPromise;
        }
        else {
            return new Promise((resolve, reject) => {
                localPromise
                    .then(() => {
                    // Sessie opkuisen
                    const uniqueKey = this.getUniqueKeyForSessionStorage(appCode, userId, setName);
                    if (appCode === null || userId === null || setName === null) {
                        let i = 0;
                        const keysToRemove = [];
                        for (i = 0; i < sessionStorage.length; i++) {
                            if (sessionStorage.key(i).indexOf(uniqueKey) == 0) {
                                keysToRemove.push(sessionStorage.key(i));
                            }
                        }
                        for (i = 0; i < keysToRemove.length; i++) {
                            sessionStorage.removeItem(keysToRemove[i]);
                        }
                    }
                    else {
                        sessionStorage.removeItem(uniqueKey);
                    }
                    this.getRemoteStore(appCode)
                        .removeItem(setName)
                        .then(() => {
                        resolve();
                    })
                        .catch((error) => {
                        reject(error);
                    });
                })
                    .catch((error) => {
                    reject('Fout bij ophalen localPromise van clearVoorkeuren: ' + error);
                });
            });
        }
    }
    confirmStorageMode(storageMode) {
        if (storageMode !== BarGvkStorageMode.BrowserOnly) {
            // in case we allow remote access we need to check if it is enabled otherwise we set it back to browser only with a warning
            if (!this.state.remoteLocalhostApiEnabled) {
                storageMode = BarGvkStorageMode.BrowserOnly;
                if (!this.warningBrowserOnlyStorageTriggered) {
                    this.warningBrowserOnlyStorageTriggered = true;
                    console.warn('GVK remote storage cannot be used in localhost session because XDC is not enabled. Browser storage will be used instead.');
                }
            }
        }
        return storageMode;
    }
    createEmptySettings(setName) {
        const settings = {
            _gvk_setName: setName,
            _gvk_timeStamp: null // Wordt bij het saven opgevuld.
        };
        return settings;
    }
    // Helper functie voor het genereren van unieke key voor de sessionStorage.
    getUniqueKeyForSessionStorage(appCode, userId, setName) {
        let key = 'domain.gvk';
        if (appCode) {
            key = key + '_' + appCode;
            if (userId) {
                key = key + '_' + userId;
                if (setName) {
                    key = key + '_' + setName;
                }
            }
        }
        return key;
    }
    getBrowserStore(appCode, userId, compatibilityMode) {
        if (compatibilityMode == BarGvkCompatibilityMode.Version_1) {
            return new BarGvkLegacyStore(appCode, userId);
        }
        else {
            return new BarGvkDefaultStore(appCode, userId);
        }
    }
    getRemoteStore(appCode) {
        if (this.remoteStore != null) {
            if (typeof this.remoteStore === 'function') {
                return this.remoteStore(appCode);
            }
            else {
                return this.remoteStore;
            }
        }
        else {
            return new BarGvkRemoteStore(this.urlRoot, appCode);
        }
    }
}

/** BAR user storage service to get and set gebruikersvoorkeuren. */
class BarGvkManager {
    constructor(
    // default state mainly used to get app and user for which we want to keep gebruikersvoorkeuren
    state = new BarGvkDefaultState(), 
    // default settings used
    settings = {
        storageMode: BarGvkStorageMode.Default
    }, 
    // here we pass an optional remote store for alternative implementations
    remoteStore = null) {
        this.state = state;
        this.settings = settings;
        this.remoteStore = remoteStore;
        this.storage = new BarGvkInternalManager(this.state, this.remoteStore);
    }
    /** Sets GVK as being disabled (globally). */
    set disabled(value) {
        this.storage.disabled = value;
    }
    /** Gets whether GVK is (globally). */
    get disabled() {
        return this.storage.disabled;
    }
    /**
     * Checks whether user storage is disabled for the application/user.
     */
    checkDisabled(appCode = this.state.appCode) {
        return this.storage.checkDisabled(true, appCode, this.state.userId);
    }
    /**
     * Loads an object from the storage.
     * @param key Key from the storage.
     */
    getItem(key, settings = this.settings) {
        return new Promise((resolve) => {
            this.storage
                .getItem(settings.appCode || this.state.appCode, this.state.userId, key, settings.storageMode, { action: 'none' }, null, settings.storageMode == BarGvkStorageMode.RemoteOnly, settings.compatibilityMode)
                .then((strData) => {
                const data = strData == null ? null : JSON.parse(strData);
                resolve(data);
            });
        });
    }
    /**
     * Saves an object to the storage.
     * @param key Key from the storage.
     * @param value The value to store in the storage.
     * @param settings Optional settings for the user storage.
     */
    setItem(key, value, settings = this.settings) {
        if (value && typeof value !== 'object')
            throw new Error('GVK item value must be a JS object.');
        return this.storage.setItem(settings.appCode || this.state.appCode, this.state.userId, key, settings.storageMode, value == null ? null : JSON.stringify(value), settings.compatibilityMode);
    }
    /**
     * Removes an object from the storage.
     * @param key Key from the storage.
     */
    removeItem(key, settings = this.settings) {
        return this.storage.removeItem(settings.appCode || this.state.appCode, this.state.userId, key, settings.storageMode, settings.compatibilityMode);
    }
}

/** GVK call settings. */
class BarGvkSettings {
    constructor() {
        /** Defines how to store the state. */
        this.storageMode = BarGvkStorageMode.Default;
        /** Defines the compatibility */
        this.compatibilityMode = BarGvkCompatibilityMode.Version_2;
    }
}

var BarClearStateTarget;
(function (BarClearStateTarget) {
    /// <summary>
    /// Clear all known BaaS/BAR caches.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["Baas"] = 0] = "Baas";
    /// <summary>
    /// Full cache clear.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["Full"] = 1] = "Full";
    /// <summary>
    /// Only clear everything related to profiel/configuration.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["Configuration"] = 2] = "Configuration";
    /// <summary>
    /// Clear everything concerning security (actie allowed).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["Security"] = 3] = "Security";
    /// <summary>
    /// Clear everything regarding caching & storage (indexed db).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["CacheStorage"] = 4] = "CacheStorage";
    /// <summary>
    /// Clear all application state in caching & storage (indexed db).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["CacheStorageApp"] = 5] = "CacheStorageApp";
    /// <summary>
    /// Clear all application/user state in caching & storage (indexed db).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["CacheStorageAppUser"] = 6] = "CacheStorageAppUser";
    /// <summary>
    /// Clear all user state in caching & storage (indexed db).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["CacheStorageUser"] = 7] = "CacheStorageUser";
    /// <summary>
    /// Clear all global state in caching & storage (indexed db).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["CacheStorageGlobal"] = 8] = "CacheStorageGlobal";
    /// <summary>
    /// Clear all state regarding gvk.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["Gvk"] = 9] = "Gvk";
    /// <summary>
    /// Clear all local storage.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["LocalStorage"] = 10] = "LocalStorage";
    /// <summary>
    /// Clear all session storage.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["SessionStorage"] = 11] = "SessionStorage";
    // https://gist.github.com/rmehner/b9a41d9f659c9b1c3340
    /// <summary>
    /// Clear all indexed db.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["IndexedDb"] = 12] = "IndexedDb";
    // https://stackoverflow.com/questions/179355/clearing-all-cookies-with-javascript
    /// <summary>
    /// Clear all cookies (except what cannot be removed due to security).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["Cookies"] = 13] = "Cookies";
})(BarClearStateTarget || (BarClearStateTarget = {}));

/*** Handles hub message to show a push notification. */
class BarClearStateHubMessageHandler {
    constructor() {
        this.action = 'ClearState';
    }
    handle(target) {
        switch (target) {
            case BarClearStateTarget.Baas: {
                this.clearCacheStorage();
                this.clearGvk(false);
                this.clearStorageByPrefix(window.sessionStorage, 'domain.gvk_');
                this.clearStorageByPrefix(window.localStorage, 'lvflanders.bar.ng.core');
                this.clearStorageByPrefix(window.sessionStorage, 'lvflanders.bar.ng.core');
                break;
            }
            case BarClearStateTarget.Cookies: {
                deleteAllCookies();
                break;
            }
            case BarClearStateTarget.SessionStorage: {
                window.sessionStorage.clear();
                break;
            }
            case BarClearStateTarget.LocalStorage: {
                window.localStorage.clear();
                break;
            }
            case BarClearStateTarget.IndexedDb: {
                deleteAllIndexedDb();
                break;
            }
            case BarClearStateTarget.Full: {
                deleteAllCookies();
                window.sessionStorage.clear();
                window.localStorage.clear();
                deleteAllIndexedDb();
                break;
            }
            case BarClearStateTarget.Gvk: {
                this.clearGvk();
                break;
            }
            case BarClearStateTarget.CacheStorageGlobal: {
                deleteIndexedDb(DB_NAME_GLOBAL);
                break;
            }
            case BarClearStateTarget.CacheStorageUser: {
                deleteIndexedDb(DB_NAME_USER);
                break;
            }
            case BarClearStateTarget.CacheStorageApp: {
                deleteIndexedDb(DB_NAME_APP);
                break;
            }
            case BarClearStateTarget.CacheStorageAppUser: {
                deleteIndexedDb(DB_NAME_APP_USER);
                break;
            }
            case BarClearStateTarget.CacheStorage: {
                this.clearCacheStorage();
                break;
            }
            case BarClearStateTarget.Configuration: {
                this.clearUserObjectStores('profiel', 'configuration');
                break;
            }
            case BarClearStateTarget.Security: {
                this.clearUserObjectStores('actieAllowed');
                break;
            }
        }
    }
    clearUserObjectStores(...objectStoreNameSuffixes) {
        return new Promise((resolve) => {
            getIndexedDbInfo(DB_NAME_USER).then((info) => {
                const objectStoresToDelete = info.objectStoreNames.filter((storeName) => {
                    return objectStoreNameSuffixes.some((suffix) => storeName.endsWith(`_${suffix}`));
                });
                if (objectStoresToDelete.length)
                    _deleteIndexedDbObjectStores(DB_NAME_USER, info.version + 1, objectStoresToDelete).then(() => resolve());
                else
                    resolve();
            });
        });
    }
    clearCacheStorage() {
        deleteIndexedDb(DB_NAME_APP);
        deleteIndexedDb(DB_NAME_APP_USER);
        deleteIndexedDb(DB_NAME_USER);
        deleteIndexedDb(DB_NAME_GLOBAL);
        deleteIndexedDb(DB_NAME_GVK);
    }
    clearGvk(clearNonLegacy = true) {
        deleteIndexedDb(DB_NAME_GVK_LEGACY);
        if (clearNonLegacy)
            deleteIndexedDb(DB_NAME_GVK);
        this.clearStorageByPrefix(window.sessionStorage, 'domain.gvk_');
    }
    clearStorageByPrefix(storage, keyPrefix) {
        Object.keys(storage)
            .filter((key) => key.startsWith(keyPrefix))
            .forEach((key) => {
            storage.removeItem(key);
        });
    }
}

class BarCustomHubMessageHandler {
    constructor() {
        this.action = 'CustomMessage';
    }
    handle(data) {
        const customEvent = new CustomEvent('customHubMessage', {
            detail: data
        });
        // Dispatch the custom event on a JavaScript object or DOM element
        window.dispatchEvent(customEvent);
    }
}

/** Manages browser notifications with service workers. */
class BarNotificationManager {
    /** Will init the notification system and ask for permissions. */
    init() {
        Notification.requestPermission((status) => {
            console.info('Notification permission status:', status);
        });
    }
    /** Shows a notification. */
    show(notification) {
        if (Notification.permission === 'granted') {
            navigator.serviceWorker.getRegistration().then((reg) => {
                if (reg)
                    reg.showNotification(notification.title, notification.options);
                else {
                    console.warn('No service worker registration retrieved to show notification.');
                }
            });
        }
    }
}

/*** Handles hub message to show a push notification. */
class BarPushNotificationHubMessageHandler {
    constructor(notificationManager = new BarNotificationManager()) {
        this.notificationManager = notificationManager;
        this.action = 'PushNotification';
    }
    handle(data) {
        this.notificationManager.show(data);
    }
}

class BarHubClient {
    constructor() {
        this.retryCount = 1; // 1 as initial call always gets fired
        this.handlers = [
            new BarPushNotificationHubMessageHandler(),
            new BarClearStateHubMessageHandler(),
            new BarCustomHubMessageHandler()
        ];
    }
    /** Adds a hub message handler. */
    addHandler(handler) {
        this.handlers.push(handler);
    }
    /** Initializes the connection to the hub and starts it. */
    init(options) {
        const neededHeaders = {
            'communication-app-code': options.appCode,
            'communication-user-token': options.securityTicketId
        };
        const connUrl = (options === null || options === void 0 ? void 0 : options.hubUrl) + '?connectionIdentifier=' + generateUuid();
        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(connUrl, { headers: neededHeaders })
            // NOTE we don't use a truthy check because 0 is a valid value (trace level)
            // by default log level none
            .configureLogging(options.logLevel == null ? signalR.LogLevel.None : options.logLevel)
            .build();
        this.connection.onclose(() => __awaiter(this, void 0, void 0, function* () {
            yield this.start({ userId: options === null || options === void 0 ? void 0 : options.userId, appCode: options.appCode, securityTicketId: options.securityTicketId });
        }));
        this.connection.on('ReceiveMessage', (message) => {
            const handlers = this.handlers.filter((h) => h.action === message.action);
            if (handlers.length)
                handlers.forEach((handler) => handler.handle(message.detail));
            else
                console.warn(`No handler found for hub messages of type ${message.action}.`, message);
        });
        this.start({ userId: options === null || options === void 0 ? void 0 : options.userId, appCode: options.appCode, securityTicketId: options.securityTicketId });
    }
    start(registration) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.connection.start();
                console.info('Connected to Bar SignalR');
            }
            catch (e) {
                console.error(e);
                if (this.retryCount >= 5) {
                    console.error('Exceeded maximum retry times for connecting SignalR Hub (5).');
                }
                else {
                    this.retryCount++;
                    setTimeout(() => this.start(registration), 2000);
                }
            }
        });
    }
}

/*
 * Public API Surface of @lvflanders/bar
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BarAuthValues, BarCacheEntryOptions, BarCacheManager, BarCacheStore, BarCacheStoreOptions, BarCacheStoreType, BarErrorHandling, BarGvkCompatibilityMode, BarGvkManager, BarGvkSettings, BarGvkStorageMode, BarHubClient, BarIdentity, BarIdentityType, BarIdpClient, BarIdpClientOptions, BarKlant, BarNotificationManager, BarSharedBootstrapper, BarStopwatch, BarStore, DB_NAME_APP, DB_NAME_APP_USER, DB_NAME_GLOBAL, DB_NAME_GVK, DB_NAME_USER, LOCALE, _BAAS_SHARED_CONSTANTS, checkDateObject, checkLocalhost, checkUrlAbsolute, concatUrl, convertToISOString, deepClone, deleteAllCookies, deleteAllIndexedDb, deleteCookie, ensureGlobal, extractUrlOrigin, extractUrlParts, generateRandomNumber, generateUuid, getParamNames, getStack, openUrl, parseQueryStringValue, readCookie, resolveUrlFromOrigin, resolveValue, versionCompare };
//# sourceMappingURL=lvflanders-bar.mjs.map
